import { format, isBefore, parseISO, startOfMonth } from "date-fns";
import { deriveCoverageMonths } from "./deriveCoverageMonths";

export interface EnrollmentDue {
  /** First-of-month date string (YYYY-MM-DD) of the missing month. */
  coverageMonth: string;
  /** Human label, e.g. "May 2025". */
  label: string;
}

/**
 * Returns the latest *completed* coverage month that has no enrollment recorded,
 * or null if the policy is up to date (or has no completed months yet).
 *
 * "Completed" means the coverage month is strictly before the current calendar
 * month, so the current open month never triggers a reminder (you record a
 * month's enrollment after it closes). This is the trigger for the client-page
 * "Enrollment due" nudge (US 5045 / ADR-047).
 *
 * NOTE: this uses the simple "last fully-completed month" rule. It does not yet
 * factor in the policy's reporting cadence / reporting_day_of_month — a future
 * refinement if the nudge needs to wait until the reporting deadline passes.
 */
export function getEnrollmentDueMonth(
  effectiveStart: string | undefined,
  effectiveEnd: string | undefined,
  recordedMonths: ReadonlySet<string>,
  today: Date
): EnrollmentDue | null {
  const months = deriveCoverageMonths(effectiveStart, effectiveEnd);
  if (months.length === 0) return null;

  const currentMonthStart = startOfMonth(today);
  const completed = months.filter((m) => isBefore(parseISO(m.coverageMonth), currentMonthStart));
  if (completed.length === 0) return null;

  const latest = completed[completed.length - 1];
  if (recordedMonths.has(latest.coverageMonth)) return null;

  return { coverageMonth: latest.coverageMonth, label: latest.label };
}

export interface EnrollmentSummary {
  /** First-of-month date string (YYYY-MM-DD) of the latest recorded month. */
  coverageMonth: string;
  /** Human label, e.g. "May 2025". */
  label: string;
  /** Recorded enrolled lives for that month. */
  lives: number;
  /** Change vs the previous recorded month; null if there is no prior month. */
  delta: number | null;
}

interface RecordedEnrollment {
  coverage_month: string;
  enrolled_lives: number;
}

/**
 * Summarizes the latest *recorded* coverage month and its change vs the prior
 * recorded month. Used for the "up to date" display so the card always shows a
 * populated figure rather than the (usually empty) current calendar month.
 * Returns null when nothing has been recorded.
 */
export function getLatestRecordedSummary(enrollments: ReadonlyArray<RecordedEnrollment>): EnrollmentSummary | null {
  if (enrollments.length === 0) return null;

  const sorted = [...enrollments].sort((a, b) => a.coverage_month.localeCompare(b.coverage_month));
  const latest = sorted[sorted.length - 1];
  const prior = sorted.length > 1 ? sorted[sorted.length - 2] : null;

  return {
    coverageMonth: latest.coverage_month,
    label: format(parseISO(latest.coverage_month), "MMM yyyy"),
    lives: latest.enrolled_lives,
    delta: prior ? latest.enrolled_lives - prior.enrolled_lives : null,
  };
}
